import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Icons } from '../lib/Icons';
import {
  addProfessionalRecommendationToWaitlist,
  dismissProfessionalRecommendation,
  getStudentEvidenceBundleV2,
} from '../lib/api';
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../lib/auth/capabilities';

const REPORT_LABELS = {
  occupational_therapy: 'Occupational Therapy',
  educational_psychology: 'Educational Psychologist',
  speech_language: 'Speech and Language',
  other: 'Other professional report',
};

export default function ProfessionalInterventionAlerts({ studentId }) {
  const { can } = useSchoolCapabilities();
  const canManage = can(SCHOOL_ACTIONS.MANAGE_INTERVENTIONS);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [busyKey, setBusyKey] = useState('');

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const result = await getStudentEvidenceBundleV2({ studentId });
      setRecommendations(result?.pendingProfessionalRecommendations || []);
    } catch (error) {
      console.error('Failed to load professional intervention alerts:', error);
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canManage) return null;
  if (loading) return null;
  if (recommendations.length === 0) return null;

  const handleWaitlist = async (rec) => {
    setBusyKey(rec.recommendationKey);
    try {
      await addProfessionalRecommendationToWaitlist({
        studentId,
        recommendationKey: rec.recommendationKey,
      });
      toast.success('Added to intervention waitlist');
      await load();
    } catch (error) {
      console.error('Failed to add to waitlist:', error);
      toast.error(error?.message || 'Could not add to waitlist');
    } finally {
      setBusyKey('');
    }
  };

  const handleDismiss = async (rec) => {
    setBusyKey(rec.recommendationKey);
    try {
      await dismissProfessionalRecommendation({
        studentId,
        recommendationKey: rec.recommendationKey,
      });
      toast.success('Recommendation dismissed');
      await load();
    } catch (error) {
      console.error('Failed to dismiss recommendation:', error);
      toast.error(error?.message || 'Could not dismiss recommendation');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <Card className="border-amber-200 dark:border-amber-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icons.Bell className="w-4 h-4 text-amber-600" />
          Intervention recommendations from professional reports
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          These recommendations were extracted from uploaded professional reports. Review and add pupils to the
          intervention waitlist where appropriate.
        </p>
        {recommendations.map((rec) => {
          const typeLabel = REPORT_LABELS[rec.reportType] || rec.reportType || 'Professional report';
          const isBusy = busyKey === rec.recommendationKey;
          return (
            <div
              key={rec.recommendationKey}
              className="rounded-lg border border-amber-100 bg-amber-50/60 p-3 dark:border-amber-900 dark:bg-amber-950/20"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{rec.text}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Source: {typeLabel}
                    {rec.status === 'on_waitlist' ? ' · Already on waitlist' : ''}
                    {rec.status === 'matched_intervention' ? ' · Active intervention in place' : ''}
                  </p>
                </div>
                {rec.status === 'pending' && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => handleWaitlist(rec)}
                      className="rounded-md bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-60"
                    >
                      Add to waitlist
                    </button>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => handleDismiss(rec)}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
